import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';

import {
   BadgeListItem,
   BottomMargin,
   DescriptionSpan,
   LightHeadline,
   StartHeadline,
} from '../../../../styledComponents/styledComponents';

import { PropTypes } from 'prop-types';

import { format_long_durations, formatTimeNumberInt } from '../../../../util/NumberFormatter';
import { IP_BLOCKLIST, SIMPLE_DATE_FORMAT, SIMPLE_TIME_FORMAT } from '../../../../constants/general';

import BootstrapTable from 'react-bootstrap-table-next';
import storePropTypes from '../../../../redux/store/propTypes';
import Modal from '../../../common/Modal/Modal';
import { DetailTableBody } from '../../../../styledComponents/ModalStyledComponents';
import {
   CopyButton,
   CopyButtonContainer,
   PayloadField,
   PayloadGrid,
   PayloadLabel,
   PayloadValue,
} from '../StyledComponents';
import { JwtAudienceOptions } from '../../../../constants/AccessLevels';
import * as browserStorage from '../../../../misc/BrowserStorageManager';
import useBlockListFetcher from '../../../../hooks/useBlockListFetcher';
import IpBlockRow from '../ip/IpBlockRow';
import TokenRevokePanel from './TokenRevokePanel';

const formatJwtTimestamp = (timestamp) => {
   const momentDate = moment.utc(timestamp);
   return !momentDate.isValid() || momentDate.unix() === 0
      ? '-'
      : momentDate.format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`);
};

const formatJwtToken = (jwt) => {
   const parts = jwt.split('.');
   return {
      header: parts[0] || '',
      payload: parts[1] || '',
      signature: parts[2] || '',
   };
};

const columns = [
   {
      text: 'Stream',
      dataField: 'streamName',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'Duration/Sum',
      dataField: 'durationSum',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: formatTimeNumberInt,
   },
   {
      text: 'Duration/Avg',
      dataField: 'durationAvg',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: formatTimeNumberInt,
   },
   {
      text: 'Sessions',
      dataField: 'sessionCount',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'IPs',
      dataField: 'ipCount',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
   },
   {
      text: 'First stream start',
      dataField: 'streamStart',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
   },
   {
      text: 'Last stream end',
      dataField: 'streamEnd',
      sort: true,
      style: { verticalAlign: 'middle', textAlign: 'right' },
      headerAlign: 'right',
      formatter: (value) => moment.utc(value).format(`${SIMPLE_DATE_FORMAT} ${SIMPLE_TIME_FORMAT}`),
   },
];

function JwtRowModal({ rowData, closeModal, onRevokeSuccess, isRevoked }) {
   const hasGuardianAccess = browserStorage.getUserInfo().aud.includes(JwtAudienceOptions.GUARDIAN);

   const { fetchBlockList, blockedMap } = useBlockListFetcher({ type: IP_BLOCKLIST });

   useEffect(() => {
      if (hasGuardianAccess && rowData.topIPs.length > 0) {
         fetchBlockList();
      }
   }, [fetchBlockList, hasGuardianAccess, rowData.topIPs.length]);

   const [copied, setCopied] = useState(false);
   const [copiedBadge, setCopiedBadge] = useState(null);
   const copyTimerRef = useRef(null);
   const jwtTimerRef = useRef(null);

   useEffect(() => () => {
      clearTimeout(copyTimerRef.current);
      clearTimeout(jwtTimerRef.current);
   }, []);

   const handleCopyBadge = async (value) => {
      await navigator.clipboard.writeText(value).catch(() => {});
      setCopiedBadge(value);
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopiedBadge(null), 1500);
   };

   const handleCopyJwt = async () => {
      await navigator.clipboard.writeText(rowData.playbackToken).catch(() => {});
      setCopied(true);
      clearTimeout(jwtTimerRef.current);
      jwtTimerRef.current = setTimeout(() => setCopied(false), 2000);
   };

   let lifetimeInSeconds = null;

   if (rowData.tokenPayload.exp && rowData.tokenPayload.iat) {
      const expMoment = moment.utc(rowData.tokenPayload.exp);
      const iatMoment = moment.utc(rowData.tokenPayload.iat);
      lifetimeInSeconds = expMoment.diff(iatMoment, 'seconds');
   }

   const formattedLifetime = format_long_durations(lifetimeInSeconds);

   let overuseSeconds = null;
   if (rowData.tokenPayload.exp && rowData.streamEnd) {
      const expMoment = moment.utc(rowData.tokenPayload.exp);
      const streamEndMoment = moment.utc(rowData.streamEnd);
      const diff = streamEndMoment.diff(expMoment, 'seconds');
      if (diff > 0) {
         overuseSeconds = diff;
      }
   }

   return (
      <Modal closeModal={closeModal} hint='Click a badge to copy its value'>
         <DetailTableBody>
            <BottomMargin pxSize={20} style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
               <DescriptionSpan fontSizePX={20} marginLeftPx={0}>
                  {'Additional details for Playback Token with ID'}
               </DescriptionSpan>
               <BadgeListItem
                  $fontSizePx={14}
                  $cursor={'pointer'}
                  onClick={() => handleCopyBadge(rowData.tokenPayload.jti)}
                  className='vertical-padding'
               >
                  {copiedBadge === rowData.tokenPayload.jti
                     ? `✓ ${rowData.tokenPayload.jti}`
                     : rowData.tokenPayload.jti}
               </BadgeListItem>
            </BottomMargin>

            <BottomMargin pxSize={15}>
               <StartHeadline>{'Playback Token Payload:'}</StartHeadline>
               <PayloadGrid>
                  <PayloadField>
                     <PayloadLabel>JTI</PayloadLabel>
                     <PayloadValue>{rowData.tokenPayload.jti}</PayloadValue>
                  </PayloadField>

                  {rowData.tokenPayload.iss && (
                     <PayloadField>
                        <PayloadLabel>Issuer</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.iss}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.aud?.length > 0 && (
                     <PayloadField>
                        <PayloadLabel>Audience</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.aud}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.iat && (
                     <PayloadField>
                        <PayloadLabel>Issued At</PayloadLabel>
                        <PayloadValue>{formatJwtTimestamp(rowData.tokenPayload.iat)}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.exp && (
                     <PayloadField>
                        <PayloadLabel>Expiration</PayloadLabel>
                        <PayloadValue>{formatJwtTimestamp(rowData.tokenPayload.exp)}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.exp && rowData.tokenPayload.iat && (
                     <PayloadField>
                        <PayloadLabel>Lifetime</PayloadLabel>
                        <PayloadValue>{formattedLifetime}</PayloadValue>
                     </PayloadField>
                  )}

                  {overuseSeconds !== null && (
                     <PayloadField>
                        <PayloadLabel>Exceeded Lifetime By</PayloadLabel>
                        <PayloadValue style={{ color: '#dc2626', fontWeight: 600 }}>
                           {format_long_durations(overuseSeconds)}
                        </PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.nbf && (
                     <PayloadField>
                        <PayloadLabel>Valid Not Before</PayloadLabel>
                        <PayloadValue>{formatJwtTimestamp(rowData.tokenPayload.nbf)}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.tag && (
                     <PayloadField>
                        <PayloadLabel>Tag</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.tag}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.user && (
                     <PayloadField>
                        <PayloadLabel>User</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.user}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.domain && (
                     <PayloadField>
                        <PayloadLabel>Domain</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.domain}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.ip && (
                     <PayloadField>
                        <PayloadLabel>IP</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.ip}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.orgahash && (
                     <PayloadField>
                        <PayloadLabel>Organization Hash</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.orgahash}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.streams?.length > 0 && (
                     <PayloadField>
                        <PayloadLabel>Streams</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.streams.join(', ')}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.forceDisconnect && (
                     <PayloadField>
                        <PayloadLabel>Force Disconnect</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.forceDisconnect.toString()}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.revocable && (
                     <PayloadField>
                        <PayloadLabel>Revocable</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.revocable.toString()}</PayloadValue>
                     </PayloadField>
                  )}

                  {rowData.tokenPayload.cust?.decoded && (
                     <PayloadField style={{ gridColumn: '1 / -1' }}>
                        <PayloadLabel>Custom Data (cust)</PayloadLabel>
                        <PayloadValue>{rowData.tokenPayload.cust.decoded}</PayloadValue>
                     </PayloadField>
                  )}
               </PayloadGrid>
            </BottomMargin>

            <BottomMargin pxSize={15}>
               <CopyButtonContainer>
                  <StartHeadline marginBottomPx={0}>{'Encoded Playback Token: '}</StartHeadline>
                  <CopyButton onClick={handleCopyJwt} title={copied ? 'Copied!' : 'Copy JWT to clipboard'}>
                     {copied ? '✓ Copied' : 'Copy token'}
                  </CopyButton>
               </CopyButtonContainer>
               <PayloadGrid>
                  {(() => {
                     const jwtParts = formatJwtToken(rowData.playbackToken);
                     return (
                        <>
                           <PayloadField>
                              <PayloadLabel>Header</PayloadLabel>
                              <PayloadValue>{jwtParts.header}</PayloadValue>
                           </PayloadField>
                           <PayloadField>
                              <PayloadLabel>Payload</PayloadLabel>
                              <PayloadValue>{jwtParts.payload}</PayloadValue>
                           </PayloadField>
                           <PayloadField>
                              <PayloadLabel>Signature</PayloadLabel>
                              <PayloadValue>{jwtParts.signature}</PayloadValue>
                           </PayloadField>
                        </>
                     );
                  })()}
               </PayloadGrid>
            </BottomMargin>
            <TokenRevokePanel playbackToken={rowData.playbackToken} tokenPayload={rowData.tokenPayload} isRevoked={isRevoked} onRevokeSuccess={onRevokeSuccess} />
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top IPs: ${rowData.topIPs.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topIPs.map((ip) =>
                  hasGuardianAccess ? (
                     <IpBlockRow
                        key={ip}
                        ip={ip}
                        isBlocked={blockedMap !== undefined ? blockedMap[ip] === true : undefined}
                        onActionSuccess={fetchBlockList}
                     />
                  ) : (
                     <BadgeListItem
                        key={ip}
                        $fontSizePx={13}
                        $cursor={'pointer'}
                        onClick={() => handleCopyBadge(ip)}
                        className='vertical-padding'
                     >
                        {copiedBadge === ip ? `✓ ${ip}` : ip}
                     </BadgeListItem>
                  ),
               )}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top providers: ${rowData.topProviders.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topProviders.map((provider) => (
                  <BadgeListItem
                     key={provider}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(provider)}
                     className='vertical-padding'
                  >
                     {copiedBadge === provider ? `✓ ${provider}` : provider}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top countries: ${rowData.topCountries.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topCountries.map((country) => (
                  <BadgeListItem
                     key={country}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(country)}
                     className='vertical-padding'
                  >
                     {copiedBadge === country ? `✓ ${country}` : country}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top referrers: ${rowData.topReferrers.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topReferrers.map((referrer) => (
                  <BadgeListItem
                     key={referrer}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(referrer)}
                     className='vertical-padding'
                  >
                     {copiedBadge === referrer ? `✓ ${referrer}` : referrer}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <BottomMargin pxSize={15}>
               <LightHeadline>{`Top browser agent ID: ${rowData.topAgents.length === 0 ? ' -' : ''}`}</LightHeadline>
               {rowData.topAgents.map((agent) => (
                  <BadgeListItem
                     key={agent}
                     $fontSizePx={13}
                     $cursor={'pointer'}
                     onClick={() => handleCopyBadge(agent)}
                     className='vertical-padding'
                  >
                     {copiedBadge === agent ? `✓ ${agent}` : agent}
                  </BadgeListItem>
               ))}
            </BottomMargin>
            <div>
               <BottomMargin pxSize={15}>
                  <LightHeadline $fontSizePx={14} marginBottomPx={10}>
                     Top Streams:
                  </LightHeadline>
                  {rowData.topStreams.map(({ streamName }) => (
                     <BadgeListItem
                        key={streamName}
                        $fontSizePx={13}
                        $cursor={'pointer'}
                        onClick={() => handleCopyBadge(streamName)}
                        className='vertical-padding'
                     >
                        {copiedBadge === streamName ? `✓ ${streamName}` : streamName}
                     </BadgeListItem>
                  ))}
               </BottomMargin>
               <BootstrapTable
                  keyField='streamName'
                  data={rowData.topStreams}
                  columns={columns}
                  classes='table-responsive-lg remove-bottom-margin'
                  id='ExtendedMisuseTable'
                  striped
                  hover
                  bootstrap4
               />
            </div>
         </DetailTableBody>
      </Modal>
   );
}

JwtRowModal.propTypes = {
   rowData: storePropTypes.jwtMisuseResultRow,
   closeModal: PropTypes.func,
   onRevokeSuccess: PropTypes.func,
   isRevoked: PropTypes.bool,
};

export default JwtRowModal;
